import { useMemo } from "react";

import { BASE_URL } from "../../data/constants";
import type { MapFeedItemDTO } from "../../data/types";
import { usePreviewAudio } from "../../Hooks/usePreviewAudio";
import { musicHeading, musicStatus } from "../../utils/venueMusic";
import { PlayPauseIcon } from "../icons/PlayPauseIcon";
import TrackCover from "../TrackCover";

type Props = {
  item: MapFeedItemDTO;
};

/** Det som spelas nu (eller senast), med preview, eller att live-data saknas. */
export default function VenueNowPlaying({ item }: Props) {
  const music = musicStatus(item);
  const track = music.kind === "live" || music.kind === "recent" ? music.track : null;

  // Previews är frivilliga. Utan en får kortet klara sig utan spelare
  // istället för att erbjuda en knapp som inte gör något.
  const previewProxyUrl = useMemo(() => {
    if (!track?.id || !track.hasPreview) return undefined;
    return `${BASE_URL}/api/preview/${track.id}`;
  }, [track?.id, track?.hasPreview]);

  const { isPlaying, toggle, failed } = usePreviewAudio(previewProxyUrl);

  if (music.kind === "noLiveData") {
    return (
      <div className="card-center card-no-live">
        <p className="text">No live data available</p>
        <p className="text-damp">
          This venue does not share what is playing right now.
        </p>
      </div>
    );
  }

  return (
    <div className="card-center">
      {track ? (
        <>
          <p className="text">{musicHeading(music)}</p>

          {/* Låtar från Beatspot Agent har bara titel och artist: utan
              omslag står en not på bildens plats, och året visas bara när
              det finns, så raden håller samma form ändå. */}
          <div className="preview-container-venue">
            <TrackCover src={track.albumCover} />

            <div className="group-column card-track-meta">
              <p className="text-l">{track.title}</p>
              <p className="text-damp">{track.artist}</p>
              {track.year && <p className="text-damper margin-top">{track.year}</p>}
            </div>

            {previewProxyUrl && !failed && (
              <button
                className="play-button"
                onClick={toggle}
                aria-label={isPlaying ? "Pause preview" : "Play preview"}
              >
                <PlayPauseIcon size={18} className="play-pause" isPlaying={isPlaying} />
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="text-damp">No track registered yet.</p>
      )}
    </div>
  );
}
